import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { FiDownload } from 'react-icons/fi';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLine } from 'victory';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Loading from 'common/services/Loading';
import Events from 'common/services/Events';
import Utils from 'common/services/Utils';
import useElementSize from 'common/hooks/useElementSize';
import DashboardService from 'api/dashboard/DashboardService';
import { PlafondConsumptionItemDto } from 'api/dashboard/dtos/PlafondConsumptionDashboardDto';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import WidgetNoData from '../components/WidgetNoData';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './PlafondConsumption.module.scss';

/** Paleta de séries obrigatória — ver docs/COLORS.md. */
const SERIES_PALETTE = ['#02c7c7', '#038cb6', '#7c5cbf', '#e8873d', '#e05a8a'];
const LIMIT_COLOR = '#e04f5f';

/** O enunciado fixa o máximo de tipologias mostradas em simultâneo. */
const MAX_SERIES = 5;

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

type TooltipState = { x: number; y: number; title: string; subtitle: string; value: string; color: string; exceeded: boolean } | null;

/**
 * Task #66600 — consumo mensal face aos plafonds definidos.
 *
 * O eixo Y é a **percentagem do plafond consumida**, não euros: um plafond de 500€ e outro de 200€
 * não são comparáveis em valor absoluto, e é a distância ao limite que interessa. Com a linha
 * tracejada nos 100% vê-se logo quem passou, e o tooltip dá os euros.
 *
 * Serve os dois contextos: no detalhe do ativo mostra os plafonds daquele ativo, no painel geral os
 * da frota.
 */
const PlafondConsumption = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<PlafondConsumptionItemDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [containerRef, { width, height }] = useElementSize();
	const [tooltip, setTooltip] = useState<TooltipState>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('plafondConsumption');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);
	// Muda sempre que a configuração de plafonds é alterada, forçando novo fetch. Sem isto era
	// preciso trocar de separador para ver um plafond acabado de criar.
	const [refreshToken, setRefreshToken] = useState(0);

	useEffect(() => {
		const subscription = Events.addListener('plafondsChanged', (vehicleId: string) => {
			// No painel geral qualquer ativo interessa; no detalhe só o que está a ser visto.
			if (!scopeVehicleId || scopeVehicleId === vehicleId) {
				setRefreshToken(prev => prev + 1);
			}
		});
		return () => subscription.remove();
	}, [scopeVehicleId]);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getPlafondConsumption(criteria);
				if (!cancelled) setData(result?.items || []);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo, scopeVehicleId, refreshToken]);

	const chart = useMemo(() => {
		const rows = data || [];
		if (rows.length === 0) { return null; }

		const months = Array.from(new Set(rows.map(x => dayjs(x.month).format('YYYY-MM')))).sort();

		const byPlafond = new Map<string, PlafondConsumptionItemDto[]>();
		rows.forEach(row => {
			const list = byPlafond.get(row.plafondId) || [];
			list.push(row);
			byPlafond.set(row.plafondId, list);
		});

		// Com mais plafonds do que séries cabem, mostram-se os que mais perto estão do limite —
		// são esses que exigem atenção. O resto é contado no rodapé para não desaparecer em silêncio.
		const ordered = Array.from(byPlafond.entries())
			.map(([plafondId, items]) => ({
				plafondId,
				items,
				peak: Math.max(...items.map(x => x.usagePercentage)),
			}))
			.sort((a, b) => b.peak - a.peak);

		const series = ordered.slice(0, MAX_SERIES).map((entry, index) => {
			const first = entry.items[0];
			return {
				plafondId: entry.plafondId,
				// No painel geral o nome do plafond sozinho é ambíguo entre ativos.
				label: scopeVehicleId ? first.designation : `${first.registrationNumber} · ${first.designation}`,
				color: SERIES_PALETTE[index % SERIES_PALETTE.length],
				points: months.map((month, monthIndex) => {
					const row = entry.items.find(x => dayjs(x.month).format('YYYY-MM') === month);
					return {
						x: monthIndex + 1,
						y: row ? row.usagePercentage : 0,
						month,
						consumed: row?.consumed ?? 0,
						limit: row?.monthlyLimit ?? first.monthlyLimit,
						exceeded: row?.exceeded ?? false,
					};
				}),
			};
		});

		const maxUsage = Math.max(100, ...rows.map(x => x.usagePercentage));

		return {
			months,
			series,
			hiddenCount: Math.max(0, ordered.length - MAX_SERIES),
			exceededCount: rows.filter(x => x.exceeded).length,
			// Folga acima do pico para a linha dos 100% e as barras não encostarem ao topo.
			maxDomain: Math.ceil((maxUsage + 10) / 10) * 10,
		};
	}, [data, scopeVehicleId]);

	/** Exporta exactamente o que o widget está a mostrar (mesmo âmbito e mesmo período). */
	const onExport = async () => {
		try {
			Loading.show();
			const criteria = props.filters ? { ...props.filters } : { dateFrom, dateTo, ...vehicleScope };
			const report = await DashboardService.getPlafondConsumptionReport(criteria);
			const title = t('vehicle.plafonds.report_title' as any) as string;
			saveAs(report, `${title}_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.xlsx`);
		} catch {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
		} finally {
			Loading.hide();
		}
	};

	const makeBarEvents = (seriesLabel: string, color: string) => ([{
		target: 'data' as const,
		eventHandlers: {
			onMouseEnter: (evt: any, targetProps: any) => {
				const datum = targetProps.datum;
				setTooltip({
					x: evt.clientX,
					y: evt.clientY,
					title: dayjs(datum.month + '-01').format('MMMM YYYY'),
					subtitle: seriesLabel,
					value: `${Utils.formatCurrency(datum.consumed)} / ${Utils.formatCurrency(datum.limit)} · ${Math.round(datum.y)}%`,
					color,
					exceeded: datum.exceeded,
				});
				return [];
			},
			onMouseMove: (evt: any) => {
				setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
				return [];
			},
			onMouseLeave: () => { setTooltip(null); return []; },
		},
	}]);

	// Largura das barras a dividir pelo nº de séries, para o grupo caber entre meses.
	const seriesCount = chart?.series.length ?? 1;
	const slotWidth = chart && width > 0 ? (width - 90) / Math.max(chart.months.length, 1) : 0;
	const barWidth = Math.max(4, Math.min(22, (slotWidth * 0.7) / seriesCount));

	return (
		<Box
			className={tableStyles.container}
			title={t('vehicle.plafonds.widget_title' as any) as string}
			subTitleTooltip={t('vehicle.plafonds.widget_tooltip' as any) as string}
			renderOptions={<div className={styles.options}>
				<FiDownload
					className={styles.exportIcon}
					title={t('vehicle.plafonds.report_title' as any) as string}
					onClick={onExport}
				/>
				<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
			</div>}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : !chart ? (
				<WidgetNoData message={t('vehicle.plafonds.widget_no_plafonds' as any) as string} />
			) : (
				<>
					<div className={styles.summary}>
						<span className={chart.exceededCount > 0 ? styles.summaryExceeded : styles.summaryOk}>
							{chart.exceededCount > 0
								? `${chart.exceededCount} ${t('vehicle.plafonds.exceeded' as any)}`
								: (t('vehicle.plafonds.within' as any) as string)}
						</span>
						{chart.hiddenCount > 0 && <span className={styles.hiddenCount}>
							{t('vehicle.plafonds.more_plafonds', { count: chart.hiddenCount }) as string}
						</span>}
					</div>

					<div className={styles.legend}>
						{chart.series.map(serie => (
							<span key={serie.plafondId} className={styles.legendItem}>
								<span className={styles.legendDot} style={{ backgroundColor: serie.color }} />
								{serie.label}
							</span>
						))}
						<span className={styles.legendItem}>
							<span className={styles.legendLine} style={{ backgroundColor: LIMIT_COLOR }} />
							{t('vehicle.plafonds.limit' as any) as string}
						</span>
					</div>

					<div className={styles.chartContainer} ref={containerRef}>
						{width > 0 && height > 0 && (
							<VictoryChart
								width={width}
								height={height}
								domain={{ y: [0, chart.maxDomain] }}
								domainPadding={{ x: Math.max(20, slotWidth / 2) }}
								padding={{ top: 12, bottom: 40, left: 50, right: 16 }}
							>
								<VictoryAxis
									style={{
										axis: { stroke: 'transparent' },
										tickLabels: { fill: '#000000', userSelect: 'none', fontSize: 11, fontFamily: 'Roboto' },
									}}
									tickValues={chart.months.map((_, i) => i + 1)}
									tickFormat={(_x, i) => dayjs(chart.months[i] + '-01').format('MMM YY')}
								/>
								<VictoryAxis
									dependentAxis
									style={{
										axis: { stroke: 'transparent' },
										grid: { stroke: '#f5f5f5' },
										tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
									}}
									tickFormat={(x: number) => `${x}%`}
								/>

								<VictoryGroup offset={barWidth + 2}>
									{chart.series.map(serie => (
										<VictoryBar
											key={serie.plafondId}
											data={serie.points}
											alignment="middle"
											barWidth={barWidth}
											cornerRadius={{ top: 2 }}
											style={{
												data: {
													fill: serie.color,
													cursor: 'pointer',
													// A cor mantém a identidade da série; o contorno vermelho
													// assinala quem passou o limite sem a perder.
													stroke: ({ datum }: any) => (datum.exceeded ? LIMIT_COLOR : 'transparent'),
													strokeWidth: 2,
												},
											}}
											// eslint-disable-next-line
											events={makeBarEvents(serie.label, serie.color)}
										/>
									))}
								</VictoryGroup>

								{/* Referência do plafond: tudo o que a ultrapassa está fora do limite. */}
								<VictoryLine
									data={[{ x: 0.5, y: 100 }, { x: chart.months.length + 0.5, y: 100 }]}
									style={{ data: { stroke: LIMIT_COLOR, strokeWidth: 1.5, strokeDasharray: '5,4' } }}
								/>
							</VictoryChart>
						)}
					</div>

					{tooltip && (
						<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
							<div className={styles.tooltipTitle}>{tooltip.title}</div>
							<div className={styles.tooltipRow}>
								<span className={styles.tooltipDot} style={{ backgroundColor: tooltip.color }} />
								<span className={styles.tooltipRowLabel}>{tooltip.subtitle}</span>
							</div>
							<div className={tooltip.exceeded ? styles.tooltipValueExceeded : styles.tooltipValue}>
								{tooltip.value}
							</div>
						</ChartTooltipPortal>
					)}
				</>
			)}
		</Box>
	);
};

export default PlafondConsumption;
